import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Download, Users, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  referral_count: number;
  early_access: boolean;
  source: string;
  created_at: string;
}

const SOURCE_COLORS: Record<string, string> = {
  organic: "#00C9B1",
  tiktok_ad: "#FF0050",
  meta_ad: "#1877F2",
  referral: "#F59E0B",
};

export default function WaitlistAdmin() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("waitlist")
        .select("*")
        .order("created_at", { ascending: false });
      if (data) setEntries(data as WaitlistEntry[]);
      setLoading(false);
    };
    fetch();
  }, []);

  // Source breakdown
  const sourceCounts = entries.reduce<Record<string, number>>((acc, e) => {
    acc[e.source || "organic"] = (acc[e.source || "organic"] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(sourceCounts).map(([name, value]) => ({ name, value }));

  // Daily signups (last 14 days)
  const dailyMap = entries.reduce<Record<string, number>>((acc, e) => {
    const day = format(new Date(e.created_at), "MMM d");
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {});
  const barData = Object.entries(dailyMap).reverse().slice(-14).map(([date, count]) => ({ date, count }));

  // Top referrers
  const topReferrers = [...entries]
    .filter((e) => e.referral_count > 0)
    .sort((a, b) => b.referral_count - a.referral_count)
    .slice(0, 10);

  const exportCSV = () => {
    const headers = "Name,Email,Source,Referral Code,Referral Count,Early Access,Signed Up\n";
    const rows = entries
      .map((e) => `"${e.name}","${e.email}","${e.source}","${e.referral_code}",${e.referral_count},${e.early_access},"${format(new Date(e.created_at), "yyyy-MM-dd HH:mm")}"`)
      .join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-primary" /> Waitlist Analytics
        </h2>
        <Button onClick={exportCSV} variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardHeader className="pb-2"><CardDescription>Total Signups</CardDescription><CardTitle className="text-3xl">{entries.length}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Organic</CardDescription><CardTitle className="text-3xl">{sourceCounts.organic || 0}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Referral</CardDescription><CardTitle className="text-3xl">{sourceCounts.referral || 0}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Early Access</CardDescription><CardTitle className="text-3xl">{entries.filter((e) => e.early_access).length}</CardTitle></CardHeader></Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Daily Signups</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                <Bar dataKey="count" fill="#00C9B1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">Signups by Source</CardTitle></CardHeader>
          <CardContent className="flex justify-center">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name} (${value})`}>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={SOURCE_COLORS[entry.name] || "#888"} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      {topReferrers.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5" /> Top 10 Referrers</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>Early Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topReferrers.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell>{e.email}</TableCell>
                    <TableCell>{e.referral_count}</TableCell>
                    <TableCell>{e.early_access ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
